import { useCallback, useEffect, useRef, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { IoKeyOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import axios from "axios";
import { PasswordRegex } from "@/utils/Regexes";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

type ChangePasswordModalProps = {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

function ChangePasswordModal({ setNeedRefetch }: ChangePasswordModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const UserToken = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string()
      .min(
        8,
        t(
          "changePasswordModal.newPasswordTooShort"
        ) as string,
      )
      .required(
        t(
          "changePasswordModal.newPasswordRequired"
        ) as string,
      )
      .matches(
        PasswordRegex,
        t(
          "changePasswordModal.newPasswordInvalid"
        ) as string,
      ),

    password: Yup.string()
      .min(
        8,
        t(
          "changePasswordModal.newPasswordTooShort"
        ) as string,
      )
      .required(t(
        "changePasswordModal.newPasswordRequired"
      ) as string)
      .matches(
        PasswordRegex,
        t(
          "changePasswordModal.newPasswordInvalid"
        ) as string,
      ),

    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), ""],
        t(
          "changePasswordModal.passwordsMustMatch"
        ) as string,
      )
      .required(
        t(
          "changePasswordModal.confirmPasswordRequired"
        ) as string,
      )
      .min(
        8,
        t(
          "changePasswordModal.newPasswordTooShort"
        ) as string,
      ),
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const { oldPassword, password } = values;

      const passwordsData = {
        currentPassword: oldPassword,
        newPassword: password,
      };

      const url = `${BASE_URL}/api/dashboard/users/password-change`;

      async function updatePassword() {
        const toastId = toast.loading(
          t(
            "changePasswordModal.changingPassword"
          ) as string,
        );

        try {
          const response = await axios.put(url, passwordsData, {
            headers: {
              Authorization: `${UserToken}`,
            },
          });

          if (!response.data.success) {
            const message =
              response.data.error ||
              (t(
                "changePasswordModal.unexpectedError"
              ) as string);
            toast.error(message);
            return;
          }

          toast.success(
            t(
              "changePasswordModal.passwordChangedSuccess"
            ) as string,
          );
          setNeedRefetch(true);
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.error ||
              (t(
                "changePasswordModal.unexpectedError"
              ) as string);
            toast.error(message);
          } else {
            toast.error(
              t(
                "changePasswordModal.unexpectedError"
              ) as string,
            );
          }
        } finally {
          toast.dismiss(toastId);
        }
      }

      updatePassword();
    },
  });

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsModalOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick, isModalOpen]);

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2 self-end rounded-md border border-sidebar bg-white px-4 py-2 text-sm font-medium capitalize text-sidebar md:px-5 md:py-2 md:text-base"
      >
        <IoKeyOutline size={18} className="mt-1" />
        {t(
          "changePasswordModal.changePassword"
        )}
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t(
                  "changePasswordModal.changePassword"
                )}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="mb-3 text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-sm text-red-600" />
              </button>
            </div>

            <form
              onSubmit={formik.handleSubmit}
              id="change-password-form"
              className="thin-scrollbar max-h-[55dvh] space-y-4 overflow-y-auto px-1"
            >
              {/* Old password */}
              <div className="mb-4">
                <label
                  htmlFor="oldPassword"
                  className="mb-2 block text-sm font-medium"
                >
                  {t(
                    "changePasswordModal.currentPassword"
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    id="oldPassword"
                    name="oldPassword"
                    value={formik.values.oldPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-12 w-full rounded-md border border-gray-300 px-4 py-2 pe-10 duration-100 focus:outline-none focus:ring-2 focus:ring-border"
                    placeholder={
                      t(
                        "changePasswordModal.currentPassword"
                      )
                    }
                  />
                  <button
                    type="button"
                    className={`absolute ${ i18n.language === "ar" ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 transform`}
                    onClick={() => setShowOldPassword(!showOldPassword)}
                  >
                    {showOldPassword ? (
                      <FaEyeSlash size={20} />
                    ) : (
                      <FaEye size={20} />
                    )}
                  </button>
                </div>
                {formik.touched.oldPassword && formik.errors.oldPassword && (
                  <p className="text-sm text-red-500">
                    {formik.errors.oldPassword}
                  </p>
                )}
              </div>

              {/* password And new Password */}
              <div className="flex flex-wrap items-start gap-x-4 sm:flex-nowrap">
                {/* New password */}
                <div className="mb-4 w-full">
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-medium"
                  >
                    {t(
                      "changePasswordModal.newPassword"
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="h-12 w-full rounded-md border border-gray-300 px-4 py-2 pe-10 duration-100 focus:outline-none focus:ring-2 focus:ring-border"
                      placeholder={
                        t(
                          "changePasswordModal.enterCurrentPassword"
                        ) as string
                      }
                    />
                    <button
                      type="button"
                      className={`absolute ${ i18n.language === "ar" ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 transform`}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FaEyeSlash size={20} />
                      ) : (
                        <FaEye size={20} />
                      )}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="text-sm text-red-500">
                      {formik.errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm password */}
                <div className="mb-4 w-full">
                  <label
                    htmlFor="confirmPassword"
                    className="mb-2 block text-sm font-medium"
                  >
                    {t(
                      "changePasswordModal.confirmPassword"
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="h-12 w-full rounded-md border border-gray-300 px-4 py-2 pe-10 duration-100 focus:outline-none focus:ring-2 focus:ring-border"
                      placeholder={
                        t(
                          "changePasswordModal.confirmNewPassword"
                        ) as string
                      }
                    />
                    <button
                      type="button"
                      className={`absolute ${ i18n.language === "ar" ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 transform`}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <FaEyeSlash size={20} />
                      ) : (
                        <FaEye size={20} />
                      )}
                    </button>
                  </div>
                  {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && (
                      <p className="text-sm text-red-500">
                        {formik.errors.confirmPassword}
                      </p>
                    )}
                </div>
              </div>
            </form>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                form="change-password-form"
                className="w-fit rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue md:text-base"
              >
                {t(
                  "changePasswordModal.changePassword"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangePasswordModal;
